import { Download, Loader2, Share2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';

type ResultsActionsBarProps = {
  isPdfDownloading: boolean;
  onShare: () => void;
  onDownloadPdf: () => void | Promise<void>;
  /**
   * Matchmaking still running. Both actions are built server-side from the
   * stored care plan, which is only persisted once the run finishes — sharing
   * or downloading now would hand over a silently incomplete guide.
   */
  isMatching?: boolean;
};

/**
 * Action row that sits below CareStageCard on the FP results page:
 * the amber Share all providers button and the sage Download care plan
 * button. The Share modal owns its own state in ResultsPage; this
 * component is purely presentational.
 */
export const ResultsActionsBar = ({
  isPdfDownloading,
  onShare,
  onDownloadPdf,
  isMatching = false,
}: ResultsActionsBarProps) => (
  <div className="flex flex-wrap items-center gap-2">
    <Button
      type="button"
      disabled={isMatching}
      onClick={onShare}
      className="h-9 gap-2 rounded-full bg-amber-200 px-4 text-sm font-medium text-amber-900 transition duration-150 ease-out hover:bg-amber-300 hover:shadow-sm active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis motion-reduce:transition-none motion-reduce:active:scale-100"
    >
      <Share2 className="h-3.5 w-3.5" aria-hidden />
      Share all providers
    </Button>
    <Button
      type="button"
      variant="outline"
      onClick={() => void onDownloadPdf()}
      disabled={isPdfDownloading || isMatching}
      className="h-9 gap-2 rounded-full border-sage bg-transparent px-4 text-sm font-medium text-sage transition duration-150 ease-out hover:bg-sage-bg hover:text-sage active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis disabled:opacity-60 motion-reduce:transition-none motion-reduce:active:scale-100"
    >
      {isPdfDownloading ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      ) : (
        <Download className="h-3.5 w-3.5" aria-hidden />
      )}
      {isPdfDownloading || isMatching ? 'Preparing care guide…' : 'Download care guide'}
    </Button>
  </div>
);
